import React from 'react';
import { TickIcon } from '../icons';
import { formatMoney, formatNumber } from '../format';

const PRESETS = [0.25, 0.5, 1];

function SizeField({ label, value, onChange }) {
    return (
        <label style={{ display: 'block' }}>
            <span className="Sel-size-label">{label}</span>
            <div className="Sel-size-control">
                <input
                    className="Sel-size-input"
                    value={value}
                    onChange={(event) => onChange(event.target.value)}
                />
                <span className="Sel-size-unit">mi</span>
            </div>
        </label>
    );
}

/**
 * What a drawn box or radius caught.
 *
 * Two tabs, because the answer to "what is in here" is two different jobs:
 * parcels to add, and parcels already held that could be refreshed. Unticking
 * a row greys the parcel out on the map, so the list and the map always agree.
 */
export default function SelectionStep({
    shapeType, size, results, isLoading, isSaving, tab, excluded, koreCheck,
    onSize, onPreset, onTab, onToggle, onToggleAll, onHoverRow, onBulk, onClear,
}) {
    const isBox = shapeType === 'box';
    const fresh = results.filter((result) => !result.existing_property);
    const known = results.filter((result) => result.existing_property);
    const shown = tab === 'new' ? fresh : known;
    const kept = shown.filter((result) => !excluded[result.parcel.parcel_id]);
    const presetOn = (miles) => (isBox ? size.widthMiles === miles && size.heightMiles === miles : size.radiusMiles === miles);

    return (
        <div className="SidePanel-step">
            <div className="Sel-size">
                <div className="Sel-size-row">
                    {isBox ? (
                        <React.Fragment>
                            <SizeField label="Width" value={size.widthMiles} onChange={(value) => onSize('widthMiles', value)} />
                            <SizeField label="Height" value={size.heightMiles} onChange={(value) => onSize('heightMiles', value)} />
                        </React.Fragment>
                    ) : (
                        <SizeField label="Radius" value={size.radiusMiles} onChange={(value) => onSize('radiusMiles', value)} />
                    )}

                    <div className="ParcelMap-spacer" />

                    <div className="Sel-presets">
                        {PRESETS.map((miles) => (
                            <button
                                key={miles}
                                type="button"
                                className={'Sel-preset' + (presetOn(miles) ? ' Sel-preset--on' : '')}
                                onClick={() => onPreset(miles)}
                            >
                                {miles} mi
                            </button>
                        ))}
                    </div>
                </div>

                <div className="Sel-help">
                    {isBox
                        ? 'Drag a corner handle to resize, or drag the shape to move it.'
                        : 'Drag the ring handle to resize, or drag the shape to move it.'}
                </div>
            </div>

            <div className="Sel-bulkbar">
                <button type="button" className="Btn Btn--sm" onClick={onToggleAll} disabled={shown.length === 0}>
                    {kept.length === shown.length && shown.length > 0 ? 'Select none' : 'Select all'}
                </button>
                <div className="ParcelMap-spacer" />
                <div className="Sel-count">{kept.length} of {shown.length} selected</div>
            </div>

            <div className="Sel-tabs">
                <button
                    type="button"
                    className={'Sel-tab' + (tab === 'new' ? ' Sel-tab--on' : '')}
                    onClick={() => onTab('new')}
                >
                    Not in iCLIMB · {fresh.length}
                </button>
                <button
                    type="button"
                    className={'Sel-tab' + (tab === 'have' ? ' Sel-tab--on' : '')}
                    onClick={() => onTab('have')}
                >
                    Already in · {known.length}
                </button>
            </div>

            <div className="Sel-list">
                {isLoading && <div className="SidePanel-loading">Looking up parcels…</div>}

                {!isLoading && shown.length === 0 && (
                    <div className="SidePanel-empty">Nothing in this shape yet.<br />Drag the handles or type a size.</div>
                )}

                {!isLoading && shown.map((result) => {
                    const { parcel, existing_property: existing } = result;
                    const on = !excluded[parcel.parcel_id];
                    const bucket = existing ? existing.status_bucket : null;

                    return (
                        <div
                            key={parcel.parcel_id}
                            className={'Sel-row' + (on ? '' : ' Sel-row--off')}
                            onClick={() => onToggle(parcel.parcel_id)}
                            onMouseEnter={() => onHoverRow(parcel.parcel_id)}
                            onMouseLeave={() => onHoverRow(null)}
                        >
                            <div className={'Check' + (on ? ' Check--on' : '')}><TickIcon /></div>
                            <div className="Sel-row-body">
                                <div className="Sel-row-addr">{existing ? existing.name || parcel.address : parcel.address}</div>
                                <div className="Sel-row-sub">
                                    {[formatNumber(parcel.building_sqft) && formatNumber(parcel.building_sqft) + ' SF', parcel.year_built, formatMoney(parcel.assessed_value)]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </div>
                            </div>
                            <span className={'Sel-row-dot' + (bucket ? ' Sel-row-dot--' + bucket : '')} />
                        </div>
                    );
                })}
            </div>

            <div className="SidePanel-footer">
                {/* A bulk add is one write per parcel and can run for several
                    seconds on a few hundred of them. Disabled alone read as the
                    button having gone dead, so it says what it is doing. */}
                <button
                    type="button"
                    className="Btn Btn--grow Btn--primary"
                    onClick={onBulk}
                    disabled={isSaving || Boolean(koreCheck) || kept.length === 0}
                >
                    {(isSaving || koreCheck) && <span className="Btn-spinner" />}
                    {koreCheck
                        ? 'Checking Kore… ' + koreCheck.done + ' of ' + koreCheck.total
                        : isSaving
                            ? (tab === 'new' ? 'Adding ' + kept.length + '…' : 'Updating ' + kept.length + '…')
                            : (tab === 'new'
                                ? 'Add ' + kept.length + ' propert' + (kept.length === 1 ? 'y' : 'ies')
                                : 'Update ' + kept.length + ' from LightBox')}
                </button>
                <button type="button" className="Btn" onClick={onClear} disabled={isSaving || Boolean(koreCheck)}>Clear</button>
            </div>
        </div>
    );
}
